import { useQuery } from "@tanstack/react-query";
import type { ComponentProps } from "react";
import { useEffect, useMemo } from "react";
import { getAppConfig } from "../api/config";
import {
  hasDifferentOrigin,
  hasHTTPScheme,
  hasSafeScheme,
  tryParseURL,
} from "../utils/url.ts";

import type { BamHooks } from "../vendor/bam-hooks";

type UTMParameters = {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
};

type LaunchExternalStrategy = {
  shouldRenderLink(url: string): boolean;
  shouldLaunchExternally(url: string): boolean;
  getExternalLinkProps(url: string, utm?: UTMParameters): ComponentProps<"a">;
};

const DEFAULT_LAUNCH_EXTERNAL_STRATEGY: LaunchExternalStrategy = {
  shouldRenderLink: (url) => hasSafeScheme(url),
  shouldLaunchExternally: (url) =>
    hasHTTPScheme(url) && hasDifferentOrigin(url),
  getExternalLinkProps: (url, utm = {}) => ({
    href: hasHTTPScheme(url) ? withUtmParameters(url, utm) : url,
    target: "_blank",
    rel: "noopener noreferrer",
  }),
};

// mlb.com and mlbshop.com are handled internally by the Ballpark app and
// intentionally bypass BamHooks.launchExternal, matching bingo's behavior.
const MLBAM_BALLPARK_INTERNAL_DOMAINS = ["mlb.com", "mlbshop.com"];

const MLB_LAUNCH_EXTERNAL_STRATEGY: LaunchExternalStrategy = {
  shouldRenderLink: (url) => hasSafeScheme(url),
  shouldLaunchExternally: (url) =>
    hasHTTPScheme(url) &&
    hasDifferentOrigin(url) &&
    !isAllowlistedMLBDomain(url),
  getExternalLinkProps: (url, utm = {}) => {
    const enrichedUrl = hasHTTPScheme(url) ? withUtmParameters(url, utm) : url;
    return {
      href: enrichedUrl,
      target: "_blank",
      rel: "noopener noreferrer",
      onClick(e: React.MouseEvent) {
        const bamhooks = (window as { BamHooks?: BamHooks }).BamHooks;

        if (!bamhooks) {
          console.warn(
            "League is MLB but window.BamHooks is unavailable; falling back to default navigation",
          );
          return;
        }

        try {
          bamhooks.launchExternal(enrichedUrl);
          e.preventDefault();
        } catch {
          console.error(
            "BamHooks.launchExternal failed; falling back to default navigation",
          );
        }
      },
    };
  },
};

const DEFAULT_UTM_PARAMETERS = {
  source: "pick3",
  campaign: "pick3",
} satisfies UTMParameters;

export function useExternalLinkProps(
  url: string | null,
  {
    utm,
  }: {
    utm: UTMParameters;
  } = { utm: {} },
) {
  const configQuery = useQuery({
    queryKey: ["config"],
    queryFn: () => getAppConfig(),
  });

  const isMLB = configQuery.data?.team?.league?.key === "MLB";

  const utmSource = utm?.source;
  const utmMedium = utm?.medium;
  const utmCampaign = utm?.campaign;
  const utmContent = utm?.content;

  const stableUtm = useMemo(() => {
    const result: UTMParameters = {};
    if (utmSource !== undefined) result.source = utmSource;
    if (utmMedium !== undefined) result.medium = utmMedium;
    if (utmCampaign !== undefined) result.campaign = utmCampaign;
    if (utmContent !== undefined) result.content = utmContent;
    return result;
  }, [utmSource, utmMedium, utmCampaign, utmContent]);

  useEffect(() => {
    if (isMLB) {
      import("../vendor/bam-hooks").catch((error) => {
        console.error("Unable to load bamhooks", error);
      });
    }
  }, [isMLB]);

  return useMemo(() => {
    if (!url) {
      return null;
    }

    const strategy = isMLB
      ? MLB_LAUNCH_EXTERNAL_STRATEGY
      : DEFAULT_LAUNCH_EXTERNAL_STRATEGY;

    if (!strategy.shouldRenderLink(url)) {
      return null;
    }

    if (strategy.shouldLaunchExternally(url)) {
      return strategy.getExternalLinkProps(url, stableUtm);
    }

    return { href: url };
  }, [url, stableUtm, isMLB]);
}

function isAllowlistedMLBDomain(href: string) {
  const url = tryParseURL(href);
  if (!url) {
    return false;
  }

  return MLBAM_BALLPARK_INTERNAL_DOMAINS.some((domain) =>
    url.host.includes(domain),
  );
}

function withUtmParameters(
  href: string,
  utm: {
    source?: string;
    medium?: string;
    campaign?: string;
    content?: string;
  },
) {
  const url = new URL(href);

  const utmWithDefaults = {
    ...DEFAULT_UTM_PARAMETERS,
    ...utm,
  };

  for (const [key, value] of Object.entries(utmWithDefaults)) {
    if (value && !url.searchParams.has(`utm_${key}`)) {
      url.searchParams.append(`utm_${key}`, value);
    }
  }

  return url.toString();
}
